import * as joint from '@clientio/rappid';

export const COMMENT_BADGE_CLASS = 'tt-flowchart-comment-badge';

const SVG_NAMESPACE = 'http://www.w3.org/2000/svg';

const createSvgElement = <K extends keyof SVGElementTagNameMap>(tagName: K): SVGElementTagNameMap[K] =>
  document.createElementNS(SVG_NAMESPACE, tagName);

const getBadgePosition = (cellView: joint.dia.CellView, width: number): joint.g.Point => {
  if (cellView.model.isLink()) {
    const midpoint = (cellView as joint.dia.LinkView).getPointAtRatio(0.5);
    return new joint.g.Point(midpoint.x - width / 2, midpoint.y - 11);
  }

  const bbox = (cellView.model as joint.dia.Element).getBBox();
  return new joint.g.Point(bbox.x + bbox.width - width / 2, bbox.y - 11);
};

/** Use the paper's transformed front layer so badges cannot enlarge cell bounds used by anchors and routers. */
export const renderCommentBadges = (paper: joint.dia.Paper, badges: { cell: joint.dia.Cell; count: number; selected: boolean }[]): void => {
  const layer = paper.getLayerNode(joint.dia.Paper.Layers.FRONT);
  layer.querySelectorAll(`.${COMMENT_BADGE_CLASS}`).forEach((badge) => badge.remove());
  badges.forEach(({ cell, count, selected }) => {
    layer.appendChild(createCommentBadge(paper.requireView(cell), count, selected));
  });
};

/** Creates a color-independent comment marker for a rendered flowchart cell. */
export const createCommentBadge = (cellView: joint.dia.CellView, count: number, selected: boolean): SVGGElement => {
  const width = count > 9 ? 42 : 36;
  const position = getBadgePosition(cellView, width);
  const badge = createSvgElement('g');
  badge.classList.add(COMMENT_BADGE_CLASS);
  badge.setAttribute('transform', `translate(${position.x}, ${position.y})`);
  badge.setAttribute('pointer-events', 'none');
  badge.setAttribute('aria-hidden', 'true');
  badge.setAttribute('filter', 'drop-shadow(0 1px 2px rgba(6, 61, 112, 0.35))');

  const background = createSvgElement('rect');
  background.setAttribute('width', String(width));
  background.setAttribute('height', '22');
  background.setAttribute('rx', '11');
  background.setAttribute('fill', selected ? '#b38600' : '#063d70');
  background.setAttribute('stroke', '#ffffff');
  background.setAttribute('stroke-width', '2');

  const icon = createSvgElement('path');
  icon.setAttribute('d', 'M7 6.5h8a2 2 0 0 1 2 2v3a2 2 0 0 1-2 2h-3.5L9 16v-2.5H7a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2Z');
  icon.setAttribute('fill', 'none');
  icon.setAttribute('stroke', '#ffffff');
  icon.setAttribute('stroke-width', '1.5');
  icon.setAttribute('stroke-linejoin', 'round');

  const countLabel = createSvgElement('text');
  countLabel.setAttribute('x', String(width - 9));
  countLabel.setAttribute('y', '11.5');
  countLabel.setAttribute('fill', '#ffffff');
  countLabel.setAttribute('font-family', 'Arial, sans-serif');
  countLabel.setAttribute('font-size', '10');
  countLabel.setAttribute('font-weight', '700');
  countLabel.setAttribute('text-anchor', 'middle');
  countLabel.setAttribute('dominant-baseline', 'middle');
  countLabel.textContent = count > 99 ? '99+' : String(count);

  badge.append(background, icon, countLabel);
  return badge;
};
